/**
 * BulkTransferItemsTable
 * Flat expandable table showing all 6 inventory types in one list.
 * Each row = one item (+ variant for products). Expanding shows lot detail inputs.
 *
 * Products:  pallets / cases / units inputs per lot (respects budget caps)
 * Chemicals: integer qty (drums/totes/bags) per lot — shows computed total
 * Generics:  integer qty per lot
 */

import React, { useState, useMemo } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  TextField, Typography, Box, IconButton, Collapse, InputAdornment, Chip, Button,
  useTheme, alpha
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Search as SearchIcon
} from '@mui/icons-material';

// ── Constants ─────────────────────────────────────────────────────────────────

const INVENTORY_TYPES = ['products', 'chemicals', 'caps', 'bottles', 'labels', 'supplies'];

const TYPE_STYLES = {
  products:  { label: 'Product',  color: '#1565c0', bg: '#e3f2fd' },
  chemicals: { label: 'Chemical', color: '#7b1fa2', bg: '#f3e5f5' },
  caps:      { label: 'Cap',      color: '#00796b', bg: '#e0f2f1' },
  bottles:   { label: 'Bottle',   color: '#2e7d32', bg: '#e8f5e9' },
  labels:    { label: 'Label',    color: '#e65100', bg: '#fff3e0' },
  supplies:  { label: 'Supply',   color: '#5d4037', bg: '#efebe9' },
};

const spinSx = {
  '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': { display: 'none' },
  '& input[type=number]': { MozAppearance: 'textfield' }
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function calcProductBase(selected, lot) {
  if (!selected) return 0;
  const upc = lot.units_per_case   || 1;
  const cpp = lot.cases_per_pallet || 1;
  return ((selected.pallets || 0) * cpp * upc) + ((selected.cases || 0) * upc) + (selected.units || 0);
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function BulkTransferItemsTable({
  availableLots, allItems, selectedItems, onQuantityChange
}) {
  const theme = useTheme();
  const [expandedRows, setExpandedRows]     = useState({});
  const [itemSearch,   setItemSearch]       = useState('');
  const [lotSearch,    setLotSearch]        = useState({}); // { 'type:key': term }

  const toggle = (rowId) => setExpandedRows(prev => ({ ...prev, [rowId]: !prev[rowId] }));

  // Build flat display rows across all types
  const flatRows = useMemo(() => {
    const rows = [];
    for (const type of INVENTORY_TYPES) {
      const typeLots = availableLots[type] || {};
      for (const [key, lots] of Object.entries(typeLots)) {
        if (!lots?.length) continue;

        let itemName, variantName, totalAvail, totalUnitQty, totalBaseQty, unitType, unitMeasure;

        if (type === 'products') {
          itemName     = lots[0]?.products?.name || 'Unknown';
          variantName  = lots[0]?.variant_name   || null;
          totalAvail   = lots.reduce((s, l) => s + (l.total_base_units || 0), 0);
        } else if (type === 'chemicals') {
          const item   = (allItems[type] || []).find(i => i.id === key);
          itemName     = item?.name || 'Unknown';
          variantName  = null;
          totalAvail   = lots.reduce((s, l) => s + (l.current_unit_quantity  || 0), 0);
          totalUnitQty = totalAvail;
          totalBaseQty = lots.reduce((s, l) => s + (l.current_total_quantity || 0), 0);
          unitType     = lots[0]?.raw_materials?.primary_unit_type    || 'units';
          unitMeasure  = lots[0]?.raw_materials?.primary_unit_measure || '';
        } else {
          const item   = (allItems[type] || []).find(i => i.id === key);
          itemName     = item?.name || 'Unknown';
          variantName  = null;
          totalAvail   = lots.reduce((s, l) => s + (l.current_quantity || 0), 0);
        }

        rows.push({
          rowId: `${type}:${key}`, type, key, lots,
          itemName, variantName,
          displayName: variantName ? `${itemName} — ${variantName}` : itemName,
          totalAvail, totalUnitQty, totalBaseQty, unitType, unitMeasure
        });
      }
    }
    return rows;
  }, [availableLots, allItems]);

  const filteredRows = useMemo(() => {
    if (!itemSearch) return flatRows;
    const s = itemSearch.toLowerCase();
    return flatRows.filter(r => r.displayName.toLowerCase().includes(s));
  }, [flatRows, itemSearch]);

  const getLotSearch = (rowId) => (lotSearch[rowId] || '').toLowerCase();
  const getFilteredLots = (rowId, lots) => {
    const s = getLotSearch(rowId);
    if (!s) return lots;
    return lots.filter(l =>
      (l.lot_number   || '').toLowerCase().includes(s) ||
      (l.batch_number || '').toLowerCase().includes(s)
    );
  };

  return (
    <Box>
      {/* Item search */}
      <TextField
        fullWidth size="small" placeholder="Search items..."
        value={itemSearch} onChange={e => setItemSearch(e.target.value)}
        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: 'primary.main' }} /></InputAdornment> }}
        sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'white' } }}
      />

      <TableContainer component={Paper} sx={{ maxHeight: 520, borderRadius: 2 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 48 }} />
              <TableCell sx={{ fontWeight: 500 }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 500 }}>Item</TableCell>
              <TableCell align="right" sx={{ fontWeight: 500 }}>Available</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRows.map(({ rowId, type, key, lots, displayName, totalAvail, totalUnitQty, totalBaseQty, unitType, unitMeasure }) => {
              const ts        = TYPE_STYLES[type] || TYPE_STYLES.supplies;
              const isExpanded = !!expandedRows[rowId];
              const filtLots  = getFilteredLots(rowId, lots);

              return (
                <React.Fragment key={rowId}>
                  {/* Summary row */}
                  <TableRow
                    hover
                    onClick={() => toggle(rowId)}
                    sx={{ cursor: 'pointer', bgcolor: isExpanded ? alpha(ts.color, 0.04) : 'inherit' }}
                  >
                    <TableCell>
                      <IconButton size="small">
                        {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={ts.label}
                        size="small"
                        sx={{
                          bgcolor: ts.bg, color: ts.color, fontWeight: 500,
                          border: `1px solid ${ts.color}55`, fontSize: '0.72rem'
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>{displayName}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 500, color: 'text.secondary', fontSize: '0.85rem' }}>
                      {type === 'chemicals'
                        ? `${totalUnitQty?.toLocaleString()} ${unitType} (${totalBaseQty?.toLocaleString()} ${unitMeasure})`
                        : type === 'products'
                          ? `${totalAvail?.toLocaleString()} units`
                          : totalAvail?.toLocaleString()
                      }
                    </TableCell>
                  </TableRow>

                  {/* Expanded detail */}
                  <TableRow>
                    <TableCell colSpan={4} sx={{ p: 0, border: 'none' }}>
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Box sx={{
                          px: 3, py: 2,
                          bgcolor: alpha(ts.color, 0.03),
                          borderTop:    `1px solid ${alpha(ts.color, 0.12)}`,
                          borderBottom: `1px solid ${alpha(ts.color, 0.12)}`
                        }}>
                          {/* Lot search */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: ts.color }}>
                              Lot/Batch Details ({filtLots.length})
                            </Typography>
                            <TextField
                              size="small" placeholder="Search lot/batch..."
                              value={lotSearch[rowId] || ''}
                              onChange={e => setLotSearch(prev => ({ ...prev, [rowId]: e.target.value }))}
                              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: ts.color }} /></InputAdornment> }}
                              sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'white', fontSize: '0.85rem' } }}
                            />
                          </Box>

                          {/* ── Product lot table ── */}
                          {type === 'products' && (
                            <ProductLotRows
                              lots={filtLots}
                              selected={selectedItems.products?.[key] || {}}
                              onQty={(lotId, val, tier) => onQuantityChange(type, key, lotId, val, tier)}
                            />
                          )}

                          {/* ── Chemical lot table ── */}
                          {type === 'chemicals' && (
                            <ChemicalLotRows
                              lots={filtLots}
                              selected={selectedItems.chemicals?.[key] || {}}
                              onQty={(lotId, val, mode) => onQuantityChange(type, key, lotId, val, 'quantity', mode)}
                            />
                          )}

                          {/* ── Generic lot table ── */}
                          {!['products', 'chemicals'].includes(type) && (
                            <GenericLotRows
                              lots={filtLots}
                              selected={selectedItems[type]?.[key] || {}}
                              onQty={(lotId, val) => onQuantityChange(type, key, lotId, val)}
                            />
                          )}

                          {filtLots.length === 0 && (
                            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                              No matching lots
                            </Typography>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}

            {filteredRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 5 }}>
                  <Typography variant="body2" color="text.secondary">
                    {itemSearch ? 'No items match your search' : 'No inventory available at this location'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

// ── Product lot rows ──────────────────────────────────────────────────────────

function ProductLotRows({ lots, selected, onQty }) {
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell sx={{ fontWeight: 500, bgcolor: 'white', fontSize: '0.8rem' }}>Lot</TableCell>
          <TableCell sx={{ fontWeight: 500, bgcolor: 'white', fontSize: '0.8rem' }}>Batch</TableCell>
          <TableCell sx={{ fontWeight: 500, bgcolor: 'white', fontSize: '0.8rem' }}>Available</TableCell>
          <TableCell sx={{ fontWeight: 500, bgcolor: 'white', fontSize: '0.8rem' }}>Transfer Qty</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {lots.map(lot => {
          const sel = selected[lot.id];
          const upc = lot.units_per_case   || 1;
          const cpp = lot.cases_per_pallet || 1;
          const budget    = lot.total_base_units || 0;
          const entPal = sel?.pallets || 0;
          const entCas = sel?.cases   || 0;
          const entUnt = sel?.units   || 0;
          const baseUsed = calcProductBase(sel, lot);

          const palletsAvail = lot.enable_pallets && (lot.pallet_quantity || 0) > 0;
          const casesAvail   = lot.enable_cases   && (lot.case_quantity   || 0) > 0;
          const unitsAvail   = (lot.unit_quantity  || 0) > 0;
          const showCases    = lot.enable_cases && (casesAvail || palletsAvail);

          const maxPal = Math.max(0, Math.min(lot.pallet_quantity || 0, Math.floor(Math.max(0, budget - entCas * upc - entUnt) / (cpp * upc))));
          const maxCas = Math.max(0, Math.min((lot.case_quantity || 0) + (lot.pallet_quantity || 0) * cpp, Math.floor(Math.max(0, budget - entPal * cpp * upc - entUnt) / upc)));
          const maxUnt = Math.max(0, budget - entPal * cpp * upc - entCas * upc);

          return (
            <TableRow key={lot.id}>
              <TableCell sx={{ fontSize: '0.85rem' }}>{lot.lot_number   || '—'}</TableCell>
              <TableCell sx={{ fontSize: '0.85rem' }}>{lot.batch_number || '—'}</TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.85rem' }}>
                  {[
                    palletsAvail && `${lot.pallet_quantity} Pallet${lot.pallet_quantity !== 1 ? 's' : ''}`,
                    casesAvail   && `${lot.case_quantity} Case${lot.case_quantity !== 1 ? 's' : ''}`,
                    unitsAvail   && `${lot.unit_quantity} Unit${lot.unit_quantity !== 1 ? 's' : ''}`
                  ].filter(Boolean).join(' · ')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {budget.toLocaleString()} units total
                </Typography>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  {palletsAvail && (
                    <TextField size="small" type="number" label={`Pallets / ${maxPal}`}
                      value={sel?.pallets || ''} onChange={e => onQty(lot.id, e.target.value, 'pallets')}
                      inputProps={{ min: 0, max: maxPal }} sx={{ width: 148, ...spinSx }} />
                  )}
                  {showCases && (
                    <TextField size="small" type="number" label={`Cases / ${maxCas}`}
                      value={sel?.cases || ''} onChange={e => onQty(lot.id, e.target.value, 'cases')}
                      inputProps={{ min: 0, max: maxCas }} sx={{ width: 148, ...spinSx }} />
                  )}
                  <TextField size="small" type="number" label={`Units / ${maxUnt}`}
                    value={sel?.units || ''} onChange={e => onQty(lot.id, e.target.value, 'units')}
                    inputProps={{ min: 0, max: maxUnt }} sx={{ width: 148, ...spinSx }} />
                  {baseUsed > 0 && (
                    <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 500 }}>
                      = {baseUsed.toLocaleString()} units
                    </Typography>
                  )}
                </Box>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

// ── Chemical lot rows ─────────────────────────────────────────────────────────

function ChemicalLotRows({ lots, selected, onQty }) {
  const [lotModes, setLotModes] = useState({});

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell sx={{ fontWeight: 500, bgcolor: 'white', fontSize: '0.8rem' }}>Lot</TableCell>
          <TableCell sx={{ fontWeight: 500, bgcolor: 'white', fontSize: '0.8rem' }}>Batch</TableCell>
          <TableCell align="right" sx={{ fontWeight: 500, bgcolor: 'white', fontSize: '0.8rem' }}>Available</TableCell>
          <TableCell align="right" sx={{ fontWeight: 500, bgcolor: 'white', fontSize: '0.8rem' }}>Transfer Qty</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {lots.map(lot => {
          const unitType    = lot.raw_materials?.primary_unit_type    || 'units';
          const unitMeasure = lot.raw_materials?.primary_unit_measure || '';
          const unitSize    = parseFloat(lot.raw_materials?.primary_unit_quantity) || 1;
          const lotMode     = unitSize > 0 ? (lotModes[lot.id] || 'container') : 'container';
          const entered     = selected[lot.id]?.quantity || 0;

          const computedDisplay = entered > 0
            ? (lotMode === 'measure'
                ? `≈ ${parseFloat((entered / unitSize).toFixed(3))} ${unitType}`
                : `= ${parseFloat((entered * unitSize).toFixed(3))} ${unitMeasure}`)
            : null;

          const handleModeSwitch = (newMode) => {
            setLotModes(prev => ({ ...prev, [lot.id]: newMode }));
            onQty(lot.id, '', newMode);
          };

          return (
            <TableRow key={lot.id}>
              <TableCell sx={{ fontSize: '0.85rem' }}>{lot.lot_number   || '—'}</TableCell>
              <TableCell sx={{ fontSize: '0.85rem' }}>{lot.batch_number || '—'}</TableCell>
              <TableCell align="right" sx={{ fontSize: '0.85rem' }}>
                {lot.current_unit_quantity || 0} {unitType}
                {unitMeasure && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    ({lot.current_total_quantity || 0} {unitMeasure})
                  </Typography>
                )}
              </TableCell>
              <TableCell align="right">
                <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                  {unitSize > 0 && (
                    <Box sx={{ display: 'flex', gap: 0.5, mb: 0.25 }}>
                      <Button
                        size="small"
                        variant={lotMode === 'container' ? 'contained' : 'outlined'}
                        onClick={() => handleModeSwitch('container')}
                        sx={{ fontSize: '0.7rem', py: 0.25, px: 0.75, minWidth: 0, textTransform: 'none' }}
                      >
                        {unitType}
                      </Button>
                      <Button
                        size="small"
                        variant={lotMode === 'measure' ? 'contained' : 'outlined'}
                        onClick={() => handleModeSwitch('measure')}
                        sx={{ fontSize: '0.7rem', py: 0.25, px: 0.75, minWidth: 0, textTransform: 'none' }}
                      >
                        {unitMeasure}
                      </Button>
                    </Box>
                  )}
                  <TextField
                    size="small" type="number"
                    value={selected[lot.id]?.quantity || ''}
                    onChange={e => onQty(lot.id, e.target.value, lotMode)}
                    inputProps={{ min: 0, max: lotMode === 'measure' ? (lot.current_total_quantity || 0) : (lot.current_unit_quantity || 0) }}
                    sx={{ width: 120, ...spinSx }}
                  />
                  {computedDisplay && (
                    <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 500 }}>
                      {computedDisplay}
                    </Typography>
                  )}
                </Box>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

// ── Generic lot rows (caps / bottles / labels / supplies) ─────────────────────

function GenericLotRows({ lots, selected, onQty }) {
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell sx={{ fontWeight: 500, bgcolor: 'white', fontSize: '0.8rem' }}>Lot</TableCell>
          <TableCell sx={{ fontWeight: 500, bgcolor: 'white', fontSize: '0.8rem' }}>Batch</TableCell>
          <TableCell align="right" sx={{ fontWeight: 500, bgcolor: 'white', fontSize: '0.8rem' }}>Available</TableCell>
          <TableCell align="right" sx={{ fontWeight: 500, bgcolor: 'white', fontSize: '0.8rem' }}>Transfer Qty</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {lots.map(lot => (
          <TableRow key={lot.id}>
            <TableCell sx={{ fontSize: '0.85rem' }}>{lot.lot_number   || '—'}</TableCell>
            <TableCell sx={{ fontSize: '0.85rem' }}>{lot.batch_number || '—'}</TableCell>
            <TableCell align="right" sx={{ fontWeight: 500, fontSize: '0.85rem' }}>
              {(lot.current_quantity || 0).toLocaleString()}
            </TableCell>
            <TableCell align="right">
              <TextField
                size="small" type="number"
                value={selected[lot.id]?.quantity || ''}
                onChange={e => onQty(lot.id, e.target.value)}
                inputProps={{ min: 0, max: lot.current_quantity || 0 }}
                sx={{ width: 100, ...spinSx }}
              />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
